import React, { useState, useEffect, useCallback } from 'react';
import {
  Store as StoreIcon, MapPin, Star, Filter,
  Search, QrCode, ArrowRight, ChevronLeft, ChevronRight, SlidersHorizontal, ChevronDown, ChevronUp
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/user/Navbar';
import Footer from '../../components/user/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { storeService, Store as StoreType, Category, StoreFilters } from '../../services/user/storeService';
import { toast } from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageHelper';

const Store: React.FC = () => {
  // Data states
  const [stores, setStores] = useState<StoreType[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(true);
  const [isCategoriesExpanded, setIsCategoriesExpanded] = useState<boolean>(false);

  // Filter states
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<number | 'All'>('All');
  const [sortBy, setSortBy] = useState<string>('highest_rated');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);



  const [heroRef, heroVisible] = useScrollAnimation();
  const [businessesRef, businessesVisible] = useScrollAnimation();

  // Debounce search query
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch Categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await storeService.getCategories();
        setCategories(response.categories || []);
      } catch (error: any) {
        console.error('Error fetching categories:', error);
        // Don't show error toast for categories - just use empty array
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);



  // Fetch Stores
  const fetchStores = useCallback(async () => {
    try {
      setLoading(true);

      const filters: StoreFilters = {
        page: currentPage,
        limit: 9, // Grid layout 3x3
        sort_by: sortBy as any,
        // Don't send country filter - let backend return all stores from all countries
      };

      if (selectedCategory !== 'All') {
        filters.category_id = selectedCategory;
      }



      // Debug: Log filters being sent to API
      console.log('🔍 Fetching stores with filters:', filters);

      const response = await storeService.getStores(filters);

      let fetchedStores = response.stores;

      // Client-side search (limited to current page, which isn't great but better than broken)
      if (debouncedSearchQuery) {
        const lowerQ = debouncedSearchQuery.toLowerCase();
        fetchedStores = fetchedStores.filter(s =>
          s.business_name.toLowerCase().includes(lowerQ) ||
          s.category_name?.toLowerCase().includes(lowerQ) ||
          s.address?.toLowerCase().includes(lowerQ)
        );
      }

      setStores(fetchedStores);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items);

      // Debug pagination
      console.log('📊 API Response Debug:', {
        totalPages: response.pagination.total_pages,
        totalItems: response.pagination.total_items,
        currentPage: response.pagination.current_page,
        perPage: response.pagination.per_page,
        storesCount: fetchedStores.length,
        storesCountries: fetchedStores.map(s => ({ name: s.business_name, country: s.country }))
      });


    } catch (error: any) {
      console.error('Error fetching stores:', error);
      // Show user-friendly error message
      if (error?.isNetworkError || error?.isConnectionRefused) {
        toast.error('Unable to connect to server. Please check your connection.');
      } else {
        toast.error('Failed to load businesses. Please try again.');
      }
      // Set empty stores array to show "no results" UI
      setStores([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedCategory, sortBy, debouncedSearchQuery]);

  useEffect(() => {
    // Reset to page 1 if filters change
    setCurrentPage(1);
  }, [selectedCategory, sortBy, debouncedSearchQuery]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);


  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Scroll to top of grid
      businessesRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8ed]">
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="pt-52 pb-32 px-4 bg-mesh-premium relative overflow-hidden">
        {/* Animated Background Blobs */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10 overflow-hidden">
          <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-[#0c513f]/5 rounded-full blur-[100px] animate-blob"></div>
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-chowdeck-pastelYellow/20 rounded-full blur-[120px] animate-blob animation-delay-2000"></div>
        </div>

        <div className={`container-premium text-center transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
          }`}>
          <div className="inline-block bg-[#0c513f] p-6 rounded-[2rem] mb-8 shadow-xl animate-float">
            <StoreIcon className="text-[#faf8ed]" size={48} />
          </div>
          <h1 className="heading-huge text-[#0c513f] mb-8 tracking-tighter-premium">
            Discover Businesses
          </h1>
          <p className="text-2xl md:text-3xl text-secondary-900 max-w-3xl mx-auto leading-tight font-medium tracking-tight">
            Explore local businesses, find what you need, and pay instantly with QR codes.
          </p>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-2 md:py-4 px-4 bg-white/80 backdrop-blur-md border-b border-[#0c513f]/10 shadow-sm transition-all duration-300">
        <div className="container-premium">
          <div className="flex flex-col md:flex-row gap-2 md:gap-6 mb-4 md:mb-8">
            {/* Search Bar */}
            <div className="flex-1 relative group">
              <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-secondary-400 group-focus-within:text-[#0c513f] transition-colors" size={20} />
              <input
                type="text"
                placeholder="Search businesses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-14 pr-6 py-2.5 border-2 border-secondary-100 rounded-2xl focus:border-[#0c513f] focus:outline-none transition-all duration-300 bg-[#faf8ed]/50 text-base font-medium placeholder:text-secondary-400 shadow-sm"
              />
            </div>

            {/* Sort Dropdown */}
            <div className="relative min-w-[200px]">
              <div className="absolute left-6 top-1/2 transform -translate-y-1/2 pointer-events-none text-secondary-500">
                <SlidersHorizontal size={18} />
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full pl-14 pr-12 py-2.5 border-2 border-secondary-100 rounded-2xl appearance-none bg-[#faf8ed]/50 focus:border-[#0c513f] focus:outline-none cursor-pointer text-sm font-bold text-secondary-800 transition-all duration-300 shadow-sm"
              >
                <option value="highest_rated">Highest Rated</option>
                <option value="most_popular">Most Popular</option>
                <option value="top_deals">Top Deals</option>
              </select>
              <div className="absolute right-6 top-1/2 transform -translate-y-1/2 pointer-events-none text-secondary-500">
                <ChevronDown size={18} />
              </div>
            </div>
          </div>

          {/* Category Filters */}
          <div className="w-full">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Filter className="text-[#0c513f] hidden md:block" size={18} />
                <span className="text-secondary-800 font-bold text-sm md:text-lg">Categories:</span>
              </div>

              {!isCategoriesExpanded && !categoriesLoading && categories.length > 0 && (
                <button
                  onClick={() => setIsCategoriesExpanded(true)}
                  className="px-4 py-1.5 rounded-full font-bold text-sm transition-all duration-300 bg-[#0c513f]/10 text-[#0c513f] hover:bg-[#0c513f] hover:text-white flex items-center gap-1"
                >
                  More <ChevronDown size={16} />
                </button>
              )}
            </div>

            <div className={`gap-1.5 md:gap-2 flex flex-wrap transition-all duration-300 ease-in-out ${isCategoriesExpanded ? 'max-h-full' : 'max-h-[38px] md:max-h-[44px] overflow-hidden'
              }`}>
              <button
                onClick={() => {
                  setSelectedCategory('All');
                  setIsCategoriesExpanded(false);
                }}
                className={`px-4 py-1.5 rounded-xl font-bold transition-all duration-300 text-sm whitespace-nowrap flex-shrink-0 border-2 ${selectedCategory === 'All'
                  ? 'bg-[#0c513f] text-[#faf8ed] border-transparent shadow-md'
                  : 'bg-white border-secondary-100 text-secondary-600 hover:border-[#0c513f] hover:text-[#0c513f] hover:bg-secondary-50'
                  }`}
              >
                All
              </button>

              {categoriesLoading ? (
                <>
                  {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-8 w-24 bg-gray-200 animate-pulse rounded-xl flex-shrink-0"></div>)}
                </>
              ) : categories.map((category) => (
                <button
                  key={category.category_id}
                  onClick={() => {
                    setSelectedCategory(category.category_id);
                    setIsCategoriesExpanded(false);
                  }}
                  className={`px-4 py-1.5 rounded-xl font-bold transition-all duration-300 text-sm whitespace-nowrap flex-shrink-0 border-2 ${selectedCategory === category.category_id
                    ? 'bg-[#0c513f] text-[#faf8ed] border-transparent shadow-md'
                    : 'bg-white border-secondary-100 text-secondary-600 hover:border-[#0c513f] hover:text-[#0c513f] hover:bg-secondary-50'
                    }`}
                >
                  {category.category_name}
                </button>
              ))}

              {/* Less Button - Only visible when expanded */}
              {isCategoriesExpanded && !categoriesLoading && (
                <button
                  onClick={() => setIsCategoriesExpanded(false)}
                  className="px-6 py-2.5 rounded-full font-bold transition-all duration-300 text-base whitespace-nowrap flex-shrink-0 bg-red-50 text-red-500 hover:bg-red-100 border-2 border-transparent flex items-center gap-2"
                >
                  Less <ChevronUp size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Businesses Grid */}
      <section ref={businessesRef} className="py-12 px-4 bg-secondary-50 min-h-[500px]">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white h-96 rounded-xl animate-pulse shadow-sm border border-secondary-100 p-6 flex flex-col gap-4">
                  <div className="h-40 bg-secondary-100 rounded-lg w-full"></div>
                  <div className="h-8 bg-secondary-100 rounded w-3/4"></div>
                  <div className="h-4 bg-secondary-100 rounded w-1/2"></div>
                  <div className="h-20 bg-secondary-100 rounded w-full"></div>
                </div>
              ))}
            </div>
          ) : stores.length === 0 ? (
            <div className="text-center py-20">
              <StoreIcon className="mx-auto text-secondary-300 mb-4" size={64} />
              <h3 className="text-2xl font-bold text-secondary-700 mb-2">
                No businesses found
              </h3>
              <p className="text-secondary-500">
                Try adjusting your filters
              </p>
            </div>
          ) : (
            <>
              <div className="mb-6 flex justify-between items-center">
                <div className="text-secondary-600">
                  Showing <span className="font-bold text-secondary-900">{stores.length}</span> business{stores.length !== 1 ? 'es' : ''}
                  {totalItems > 0 && (
                    <span> of <span className="font-bold text-secondary-900">{totalItems}</span> total</span>
                  )}
                </div>
                {totalPages > 1 && (
                  <div className="text-sm text-secondary-500">
                    Page <span className="font-semibold text-secondary-700">{currentPage}</span> of <span className="font-semibold text-secondary-700">{totalPages}</span>
                  </div>
                )}
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {stores.map((business, index) => (
                  <Link
                    key={business.business_id}
                    to={`/store/${business.business_id}`}
                    className={`bg-white rounded-[2.5rem] p-4 border-2 border-transparent hover:border-[#0c513f]/10 shadow-sm hover:shadow-2xl transition-all duration-500 group hover:scale-[1.02] hover:-translate-y-2 block ${businessesVisible
                      ? `animate-fade-in-up opacity-100`
                      : 'opacity-0'
                      }`}
                    style={{ transitionDelay: `${index * 50}ms` }}
                  >
                    {/* Business Image/Icon */}
                    <div className="relative bg-[#faf8ed] h-60 rounded-[2rem] flex items-center justify-center overflow-hidden mb-4">
                      {getImageUrl(business.banner_image) ? (
                        <>
                          <img
                            src={getImageUrl(business.banner_image) || ''}
                            alt={business.business_name}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const parent = e.currentTarget.parentElement;
                              if (parent) {
                                const fallback = parent.querySelector('.fallback-banner');
                                if (fallback) (fallback as HTMLElement).style.display = 'flex';
                              }
                            }}
                          />
                          <div className="fallback-banner hidden text-7xl opacity-30 grayscale group-hover:grayscale-0 transition-all duration-500 w-full h-full items-center justify-center">
                            {business.category_icon ? <img src={getImageUrl(business.category_icon) || business.category_icon} className="w-24 h-24 object-contain" /> : '🏢'}
                          </div>
                        </>
                      ) : (
                        <div className="text-7xl opacity-30 grayscale group-hover:grayscale-0 transition-all duration-500">
                          {business.category_icon ? <img src={getImageUrl(business.category_icon) || business.category_icon} className="w-24 h-24 object-contain" /> : '🏢'}
                        </div>
                      )}

                      {/* Distance Badge if available */}
                      {business.distance_km && (
                        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur text-[#0c513f] text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-sm">
                          <MapPin size={12} />
                          {business.distance_km} km
                        </div>
                      )}

                      {/* Status Badge */}
                      <div className={`absolute top-4 left-4 text-xs font-bold px-4 py-2 rounded-full shadow-sm ${business.is_open
                        ? 'bg-[#0c513f] text-white'
                        : 'bg-red-50 text-red-500'
                        }`}>
                        {business.is_open ? 'Open Now' : 'Closed'}
                      </div>
                    </div>

                    {/* Business Info */}
                    <div className="px-4 pb-2">
                      {/* Category Badge */}
                      <div className="mb-4 flex justify-between items-start">
                        <span className="bg-[#faf8ed] text-[#0c513f] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
                          {business.category_name}
                        </span>
                        {business.country && (
                          <span className="text-xs text-secondary-500 font-bold bg-secondary-50 px-3 py-1.5 rounded-full">
                            {business.country}
                          </span>
                        )}
                      </div>

                      {/* Business Name */}
                      <h3 className="text-2xl font-black text-[#0c513f] mb-2 group-hover:text-primary-600 transition-colors line-clamp-1 tracking-tight">
                        {business.business_name}
                      </h3>

                      {/* Rating */}
                      <div className="flex items-center gap-2 mb-4">
                        <div className="flex items-center bg-[#fff9e6] px-2 py-1 rounded-lg">
                          <Star className="text-yellow-500 fill-yellow-500" size={16} />
                          <span className="ml-1 font-black text-[#0c513f] text-sm">{typeof business.rating === 'number' ? business.rating.toFixed(1) : business.rating}</span>
                        </div>
                        <span className="text-secondary-400 text-xs font-bold">({business.total_reviews} reviews)</span>
                      </div>

                      <div className="space-y-3 mb-6 text-sm text-secondary-600 font-medium">
                        <div className="flex items-start gap-3">
                          <MapPin className="text-[#0c513f] mt-0.5 flex-shrink-0 opacity-50" size={16} />
                          <span className="line-clamp-2">{business.address}, {business.city}</span>
                        </div>
                      </div>

                      {/* Action Button */}
                      <div className="w-full bg-[#0c513f] text-white px-6 py-4 rounded-[1.5rem] font-bold text-lg transition-all duration-300 flex items-center justify-between group-hover:shadow-lg group-hover:bg-[#0a4535]">
                        <span className="flex items-center gap-2">
                          <QrCode size={20} />
                          View
                        </span>
                        <div className="bg-white/20 p-1 rounded-full">
                          <ArrowRight size={20} className="-rotate-45 group-hover:rotate-0 transition-transform duration-300" />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages >= 1 && stores.length > 0 && (
                <div className="flex justify-center items-center mt-20 gap-4">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="w-14 h-14 border-2 border-[#0c513f]/10 rounded-full flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:border-[#0c513f] hover:bg-[#0c513f] hover:text-white transition-all duration-300"
                  >
                    <ChevronLeft size={24} />
                  </button>

                  <div className="flex gap-3 bg-white p-2 rounded-full shadow-sm">
                    {(() => {
                      const pages = [];
                      const maxButtons = 5; // Reduced max buttons for cleaner look
                      // ... (Pagination logic simplified for brevity, kept mostly same logic but updated styles)
                      // Re-implementing simplified logic for clarity within this tool call:

                      if (totalPages <= maxButtons) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(
                            <button key={i} onClick={() => handlePageChange(i)} className={`w-12 h-12 rounded-full font-black text-lg transition-all ${currentPage === i ? 'bg-[#0c513f] text-white shadow-lg scale-110' : 'text-secondary-500 hover:bg-secondary-100'}`}>{i}</button>
                          );
                        }
                      } else {
                        // Simple ellipses logic for robust replacement
                        pages.push(<button key={1} onClick={() => handlePageChange(1)} className={`w-12 h-12 rounded-full font-black text-lg transition-all ${currentPage === 1 ? 'bg-[#0c513f] text-white shadow-lg scale-110' : 'text-secondary-500 hover:bg-secondary-100'}`}>1</button>);
                        if (currentPage > 3) pages.push(<span key="s" className="w-10 flex justify-center items-center">...</span>);

                        const start = Math.max(2, currentPage - 1);
                        const end = Math.min(totalPages - 1, currentPage + 1);
                        for (let i = start; i <= end; i++) {
                          pages.push(<button key={i} onClick={() => handlePageChange(i)} className={`w-12 h-12 rounded-full font-black text-lg transition-all ${currentPage === i ? 'bg-[#0c513f] text-white shadow-lg scale-110' : 'text-secondary-500 hover:bg-secondary-100'}`}>{i}</button>);
                        }

                        if (currentPage < totalPages - 2) pages.push(<span key="e" className="w-10 flex justify-center items-center">...</span>);
                        pages.push(<button key={totalPages} onClick={() => handlePageChange(totalPages)} className={`w-12 h-12 rounded-full font-black text-lg transition-all ${currentPage === totalPages ? 'bg-[#0c513f] text-white shadow-lg scale-110' : 'text-secondary-500 hover:bg-secondary-100'}`}>{totalPages}</button>);
                      }

                      return pages;
                    })()}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="w-14 h-14 border-2 border-[#0c513f]/10 rounded-full flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:border-[#0c513f] hover:bg-[#0c513f] hover:text-white transition-all duration-300"
                  >
                    <ChevronRight size={24} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Store;


